import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tag, Plus, Trash2, Power, AlertCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function VouchersManager({ getHeaders }) {
  const [vouchers, setVouchers] = useState([]);
  const [code, setCode] = useState('');
  const [discountType, setDiscountType] = useState('percent');
  const [discountValue, setDiscountValue] = useState('10');
  const [maxUses, setMaxUses] = useState('');
  const [validUntil, setValidUntil] = useState('');
  const [loading, setLoading] = useState(false);

  const fetchVouchers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/vouchers`, {
        headers: getHeaders(), withCredentials: true,
      });
      setVouchers(res.data || []);
    } catch { /* ignore */ }
  }, [getHeaders]);

  useEffect(() => { fetchVouchers(); }, [fetchVouchers]);

  const submit = async (e) => {
    e.preventDefault();
    if (!code.trim()) { toast.error('Code requis'); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/admin/vouchers`, {
        code: code.trim().toUpperCase(),
        discount_type: discountType,
        discount_value: parseFloat(discountValue),
        max_uses: maxUses ? parseInt(maxUses, 10) : null,
        valid_until: validUntil || null,
      }, { headers: { ...getHeaders(), 'Content-Type': 'application/json' }, withCredentials: true });
      toast.success(`Code ${code.toUpperCase()} cree`);
      setCode(''); setMaxUses(''); setValidUntil(''); setDiscountValue('10');
      fetchVouchers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally { setLoading(false); }
  };

  const remove = async (id, c) => {
    if (!window.confirm(`Supprimer le code ${c} ?`)) return;
    try {
      await axios.delete(`${API}/admin/vouchers/${id}`, { headers: getHeaders(), withCredentials: true });
      toast.success('Code supprime');
      fetchVouchers();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const toggle = async (id) => {
    try {
      await axios.put(`${API}/admin/vouchers/${id}/toggle`, {}, { headers: getHeaders(), withCredentials: true });
      fetchVouchers();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  return (
    <div className="space-y-5" data-testid="vouchers-manager">
      {/* Form */}
      <div className="bg-white border border-[#E2E2DF] rounded-xl p-5">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-[#C77D63]/10 flex items-center justify-center flex-shrink-0">
            <Tag className="w-5 h-5 text-[#C77D63]" />
          </div>
          <div>
            <h3 className="font-heading text-base font-bold text-[#1A3C34]">Creer un code promo</h3>
            <p className="text-xs text-[#737373] mt-0.5">
              Vos clients pourront entrer ce code lors de leur reservation pour beneficier d'une reduction.
            </p>
          </div>
        </div>
        <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-[1fr_140px_120px_140px_140px_auto] gap-3 items-end">
          <div>
            <label className="text-xs font-semibold text-[#1A3C34]">Code</label>
            <Input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="EX: WELCOME10" className="h-10 mt-1 border-[#E2E2DF] uppercase" data-testid="voucher-code-input" required />
          </div>
          <div>
            <label className="text-xs font-semibold text-[#1A3C34]">Type</label>
            <Select value={discountType} onValueChange={setDiscountType}>
              <SelectTrigger className="h-10 mt-1 border-[#E2E2DF]" data-testid="voucher-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="percent">Pourcentage</SelectItem>
                <SelectItem value="fixed">Montant fixe</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-semibold text-[#1A3C34]">Valeur</label>
            <Input type="number" step="0.01" value={discountValue} onChange={(e) => setDiscountValue(e.target.value)} placeholder={discountType === 'percent' ? '10' : '5.00'} className="h-10 mt-1 border-[#E2E2DF]" data-testid="voucher-value-input" required />
          </div>
          <div>
            <label className="text-xs font-semibold text-[#1A3C34]">Util. max</label>
            <Input type="number" min="1" value={maxUses} onChange={(e) => setMaxUses(e.target.value)} placeholder="Illimite" className="h-10 mt-1 border-[#E2E2DF]" />
          </div>
          <div>
            <label className="text-xs font-semibold text-[#1A3C34]">Expire le</label>
            <Input type="date" value={validUntil} onChange={(e) => setValidUntil(e.target.value)} className="h-10 mt-1 border-[#E2E2DF]" />
          </div>
          <Button type="submit" disabled={loading} className="h-10 bg-[#C77D63] text-white hover:bg-[#b06a51] font-bold" data-testid="voucher-submit">
            <Plus className="w-4 h-4 mr-1" /> Creer
          </Button>
        </form>
      </div>

      {/* List */}
      <div className="bg-white border border-[#E2E2DF] rounded-xl p-5">
        <h3 className="font-heading text-base font-bold text-[#1A3C34] mb-3">Codes existants ({vouchers.length})</h3>
        {vouchers.length === 0 ? (
          <p className="text-sm text-[#737373] flex items-center gap-2"><AlertCircle className="w-4 h-4" /> Aucun code promo cree.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] uppercase text-[#737373] border-b border-[#E2E2DF]">
                  <th className="py-2">Code</th>
                  <th className="py-2">Reduction</th>
                  <th className="py-2">Utilisations</th>
                  <th className="py-2">Expire</th>
                  <th className="py-2">Statut</th>
                  <th className="py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {vouchers.map(v => (
                  <tr key={v.voucher_id} className="border-b border-[#E2E2DF] last:border-b-0" data-testid={`voucher-row-${v.code}`}>
                    <td className="py-3 font-mono font-bold text-[#1A3C34]">{v.code}</td>
                    <td className="py-3 text-[#1A3C34]">
                      {v.discount_type === 'percent' ? `${v.discount_value}%` : `${v.discount_value} EUR`}
                    </td>
                    <td className="py-3 text-[#737373]">
                      {v.used_count}{v.max_uses ? ` / ${v.max_uses}` : ' / illimite'}
                    </td>
                    <td className="py-3 text-[#737373]">{v.valid_until || '—'}</td>
                    <td className="py-3">
                      {v.active ? (
                        <span className="text-green-700 bg-green-50 px-2 py-0.5 rounded text-[10px] font-semibold">ACTIF</span>
                      ) : (
                        <span className="text-[#737373] bg-[#F0EFEA] px-2 py-0.5 rounded text-[10px] font-semibold">INACTIF</span>
                      )}
                    </td>
                    <td className="py-3 text-right">
                      <button onClick={() => toggle(v.voucher_id)} className="text-[#737373] hover:text-[#1A3C34] p-1.5 rounded hover:bg-[#F0EFEA]" title={v.active ? 'Desactiver' : 'Activer'} data-testid={`voucher-toggle-${v.code}`}>
                        <Power className="w-4 h-4" />
                      </button>
                      <button onClick={() => remove(v.voucher_id, v.code)} className="text-red-500 hover:text-red-600 p-1.5 rounded hover:bg-red-50 ml-1" title="Supprimer" data-testid={`voucher-delete-${v.code}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
